import { useEffect, useState } from "react";
import { Typography } from "antd";
import { useSelector } from "react-redux";
import getIcon from "../../../../utils/Icon";
import CoinBadge from "../../../../components/coin_badge/CoinBadge";
import PromoTable from "./PromoTable";
import PromoWinners from "./PromoWinners";
import feeApi from "../../../../api/feeApi";
import PromoCountdown from "../../../../components/PromoCountdown/PromoCountdown";

export default function PromoShow({ create }) {
    const promoForm = useSelector((state) => state.promoForm);
    const promoType = useSelector((state) => state.promoForm.promoType);
    const BASE_URL = "https://api.duotetra.quest";
    const [fee, setFee] = useState(null);
    const [discount, setDiscount] = useState({ discountPercentage: 0, isApplied: false });

    const promoter = promoForm.promoters?.[0];
    
    // Определяем иконку кристалла по категории
    const getCrystalIcon = () => {
        const count = parseInt((promoForm?.category?.category_name || '').split(' ')[0], 10);
        if (count > 6) return getIcon('diamond');
        if (count >= 4) return getIcon('crown');
        if (count >= 1) return getIcon('star_big');
        return getIcon('star_big');
    };
    
    const totalCrystalBudget = promoForm?.category?.positions?.reduce((sum, p) => {
        const budget = Number(p?.budget);
        if (!Number.isNaN(budget) && budget > 0) {
            return sum + budget;
        }
        const winners = Number(p?.winners) || 0;
        const drop = Number(p?.dropPerWinner) || 0;
        return sum + (winners * drop);
    }, 0) || 0;

    useEffect(() => {
        if (create && promoForm?.participantsCount) {
            const calculateInterpolatedFee = async () => {
                try {
                    const allFees = await feeApi.feeLevelsApi.getAll();
                    if (!allFees?.length) return;
                    const isOnlyCrystals = promoType === "onlyCrystals";
                    const filtered = allFees
                        .filter(f => isOnlyCrystals ? f.onlyGem : !f.onlyGem)
                        .sort((a, b) => a.max - b.max);
                    const count = promoForm.participantsCount;
                    let resultFee = filtered[0]?.fee ?? 0;
                    for (let i = 1; i < filtered.length; i++) {
                        if (count <= filtered[i].max) {
                            const prev = filtered[i - 1];
                            const curr = filtered[i];
                            const inc = (curr.fee - prev.fee) / (curr.max - prev.max);
                            resultFee = prev.fee + inc * (count - prev.max);
                            break;
                        }
                    }
                    if (count > filtered[filtered.length - 1].max) {
                        resultFee = filtered[filtered.length - 1].fee;
                    }
                    setFee(resultFee.toFixed(2));
                } catch (e) {
                    console.error("Ошибка получения fee уровней:", e);
                }
            };
            calculateInterpolatedFee();
        }
    }, [create, promoForm.participantsCount, promoType]);

    useEffect(() => {
        if (!create && promoForm.id && promoForm.participantsCount != null) {
            (async () => {
                try {
                    const ds = await feeApi.getDiscountStatus(promoForm.id);
                    setDiscount(ds);
                    const rawFee = promoType === "onlyCrystals"
                        ? await feeApi.calculateFeeGem(promoForm.id, promoForm.participantsCount)
                        : await feeApi.calculateFee(promoForm.id, promoForm.participantsCount);
                    setFee(rawFee.toFixed(2));
                } catch (e) {
                    console.error("Ошибка при получении комиссии/скидки:", e);
                    setFee(null);
                    setDiscount({ discountPercentage: 0, isApplied: false });
                }
            })();
        }
    }, [create, promoForm.id, promoForm.participantsCount, promoType]);

    const showCard = (title, subtitle) => (
        <div className="w-full h-fit flex flex-col p-2">
            <div className="w-full flex flex-row ml-2">
                <div className="w-fit h-fit flex flex-col gap-2">
                    <div className="relative w-[40px] ml-2">
                        <img
                            width={40}
                            className="rounded-full"
                            src={
                                promoter?.logo
                                    ? `${BASE_URL}${promoter.logo}`
                                    : getIcon("dog")
                            }
                            alt="promoter"
                        />
                        <img
                            width={15}
                            className="absolute right-0 -bottom-1 rounded-full"
                            src={getIcon("octo")}
                        />
                    </div>
                    <div className="mt-2 ml-2 flex w-full flex-row items-center gap-0 rounded-md border border-white/10 bg-white/[0.08]">
                        <div className="ml-1 h-[20px] w-[4px] rounded-sm bg-emerald-500/70" />
                        <Typography className="w-full text-center text-[12px] font-semibold text-white/90">
                            {promoForm.category?.category_name}
                        </Typography>
                    </div>
                </div>
                <div className="w-fit h-fit flex flex-col ml-8">
                    <div className="w-full flex flex-row items-center gap-4">
                        <div className="flex flex-col">
                            <Typography className="text-white font-semibold">
                                {promoter?.name || title}
                            </Typography>
                            <Typography className="text-[12px] font-thin text-white/45">
                                {subtitle}
                            </Typography>
                        </div>
                        <PromoCountdown startDate={promoForm.startDate} durationDays={promoForm.durationDays} />
                    </div>
                    <div className="ml-2 flex flex-row items-center gap-2 mt-2">
                        {promoType === "onlyCrystals" ? (
                            <div className="h-[30px] rounded-2xl bg-gradient-to-r from-purple-500 via-pink-500 to-yellow-500 p-[1px]">
                                <div className="flex h-full w-full items-center justify-center gap-1 rounded-2xl bg-[#070A12]/90 px-2 backdrop-blur-sm">
                                    <span className="text-white text-[12px] font-medium">{totalCrystalBudget}</span>
                                    <img src={getCrystalIcon()} alt="crystal" className="w-[18px] h-[18px] object-contain"/>
                                    <span className="text-white text-[12px]">Crystals Giveaway</span>
                                </div>
                            </div>
                        ) : (
                            <CoinBadge
                                isGiveaway={true}
                                icon={promoForm.tokenLogoUrl || promoForm.tokenType}
                                name={promoForm.tokenType}
                                count={promoForm.totalAmount}
                            />
                        )}
                        <div className="relative inline-flex w-fit items-center rounded-lg border border-white/10 bg-white/[0.08] px-2 py-[2px] text-[12px] font-medium text-emerald-100">
                            {fee !== null ? `$${fee}` : "..."}
                            {discount.isApplied && discount.discountPercentage > 0 && (
                                <span className="absolute -right-2 -top-2 rounded border border-emerald-400/30 bg-emerald-500/25 px-1 text-[10px] font-medium text-emerald-100">
                                    −{discount.discountPercentage}%
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="h-fit w-full max-w-[450px] rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-xl">
            {showCard("Neiro", "promoter and sponsor")}
            <div className="w-full p-2">
                <PromoTable />
                <PromoWinners />
            </div>
        </div>
    );
}
